import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ServerSideSearchableSelect } from './ServerSideSearchableSelect';
import { useServiceAreasWithPagination } from '@/shared/hooks/useServiceAreasWithPagination';
import { Option } from './SearchableSelect';
import { apiClient } from '@/shared/api/client';
import { apiRoutes } from '@/shared/api/routes';

interface ServiceAreaSelectProps {
  id?: string;
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  'data-testid'?: string;
  enabled?: boolean;
  multiple?: boolean;
  /**
   * When true, dropdown list options are wrapped in `<a>` tags pointing to
   * `/system/service-areas/{id}`. Plain click toggles selection;
   * Cmd/Ctrl/middle-click opens the service area detail page in a new tab
   * (legacy DRT parity). Trigger chips stay plain text regardless.
   */
  linkItems?: boolean;
}

/**
 * ServiceAreaSelect - A reusable component for selecting service areas with pagination and search
 * 
 * Features:
 * - Server-side pagination (loads 10 items at a time)
 * - Server-side search with 300ms debounce (searches by name)
 * - Multi-select with checkboxes
 * - Auto-loads more on scroll
 * 
 * @example
 * ```tsx
 * <ServiceAreaSelect
 *   value={selectedServiceAreas}
 *   onChange={(areas) => setSelectedServiceAreas(areas)}
 *   placeholder="Select Service Areas"
 *   enabled={true}
 * />
 * ```
 */
export const ServiceAreaSelect: React.FC<ServiceAreaSelectProps> = ({
  id = 'service-area-select',
  value,
  onChange,
  placeholder = 'Select Service Areas',
  'data-testid': dataTestId = 'service-area-select',
  enabled = true,
  multiple = true,
  linkItems = true,
}) => {
  const {
    serviceAreaOptions,
    handleLoadMore,
    hasNextPage,
    isFetchingNextPage,
    handleSearchChange,
  } = useServiceAreasWithPagination(enabled);

  const missingSelectedIds = useMemo(() => {
    if (!value || value.length === 0) {
      return [];
    }

    const loadedIdSet = new Set(serviceAreaOptions.map((option) => String(option.id)));
    return value.filter((selectedId) => !loadedIdSet.has(selectedId));
  }, [serviceAreaOptions, value]);

  const { data: preloadedOptions = [] } = useQuery<Option[]>({
    queryKey: ['serviceAreas', 'selected-options', missingSelectedIds],
    queryFn: async () => {
      const response = await apiClient.post(apiRoutes.proxy('support/internal/service_area/select_query'), {
        select: ['id', 'name', 'enclosure.name'],
        query: [['in', 'id', missingSelectedIds.map(Number)]],
        options: {
          timeout: 10,
          offset: 0,
          limit: missingSelectedIds.length,
          order: [['enclosure.name', 'asc_nulls_last']],
          calculate_total: false,
          return_results: true,
          select_distinct: true,
        },
      });

      return (response.data?.data || []).map((row: any[]) => ({
        id: String(row[0] ?? ''),
        name: String(row[2] || row[1] || 'Unknown Service Area'),
      }));
    },
    enabled: enabled && missingSelectedIds.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  const mergedOptions = useMemo(() => {
    const merged = [...serviceAreaOptions];
    const seenIds = new Set(serviceAreaOptions.map((option) => String(option.id)));

    preloadedOptions.forEach((option) => {
      const optionId = String(option.id);
      if (!seenIds.has(optionId)) {
        seenIds.add(optionId);
        merged.push(option);
      }
    });

    return merged;
  }, [preloadedOptions, serviceAreaOptions]);

  const handleChange = (selected: Option[]) => {
    const selectedIds = selected.map(s => String(s.id));
    onChange(selectedIds);
  };

  const selectedOptions = useMemo(() => {
    const valueSet = new Set(value || []);
    return mergedOptions.filter((option) => valueSet.has(String(option.id)));
  }, [mergedOptions, value]);

  const initialValue = useMemo(
    () => mergedOptions.filter((option) => (value || []).includes(String(option.id))),
    [mergedOptions, value]
  );

  return (
    <ServerSideSearchableSelect
      id={id}
      options={mergedOptions}
      value={selectedOptions}
      selectedIds={value || []}
      initialValue={initialValue}
      onChange={handleChange}
      placeholder={placeholder}
      onLoadMore={handleLoadMore}
      hasNextPage={hasNextPage}
      isFetchingNextPage={isFetchingNextPage}
      onSearchChange={handleSearchChange}
      data-testid={dataTestId}
      multiple={multiple}
      {...(linkItems ? { getItemUrl: (item) => `/system/service-areas/${item.id}` } : {})}
    />
  );
};
